import { Component, inject, signal, computed, effect, output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { filterStatus, groupBy, ServiceConditionService, SettingsService } from '@lp-ui-lib';
import { AssetsService, logError } from '@lp-ui-lib';
import { UiService } from '@lp-ui-lib';
import { I18nService } from '@lp-ui-lib';
import { LocationFilter } from '@lp-ui-lib';
import { Asset } from '@lp-ui-lib';
import { App_Routes } from '@lp-ui-lib';
import { HeaderComponent } from '@lp-ui-lib';
import { TabBarComponent } from '../../components/tab-bar/tab-bar';
import { TabBarItemComponent } from '../../components/tab-bar/tab-bar-item/tab-bar-item';
import { SearchFilterComponent } from '@lp-ui-lib';
import { PlantListComponent } from './plant-list/plant-list';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { MapOverlayComponent } from './map-overlay/map-overlay.component';
import { MapComponent } from '../../components/map/map';
import { GLOBAL_CONSTANT } from '@lp-ui-lib';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { ServiceConditionsModalComponent } from '@lp-ui-lib';

@Component({
  selector: 'app-homepage',
  imports: [
    HeaderComponent,
    TabBarComponent,
    TabBarItemComponent,
    SearchFilterComponent,
    PlantListComponent,
    NzAlertModule,
    MapOverlayComponent,
    MapComponent,
    NzIconModule,
    ServiceConditionsModalComponent,
  ],
  templateUrl: './homepage.html',
  styleUrls: ['./homepage.scss'],
})
export class HomepageComponent {
  constructor() {
    effect(() => {
      this.isVisible.set(this.serviceConditionService.isBannerVisible());
    });
  }
  // Inject services
  private assetsService = inject(AssetsService);
  private settingService = inject(SettingsService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private ui = inject(UiService);
  public i18n = inject(I18nService);
  isVisible = signal(false);
  public serviceConditionService = inject(ServiceConditionService);

  // Tabs for UI
  tabs = ['map', 'list'];

  // Signals
  // plants = signal<any[]>([]);
  loading = signal<boolean>(true);
  loadingThumbnails = signal<boolean>(false);
  filteredPlants = signal<boolean>(false);
  activeTab = signal<number>(0);
  homePageFilterStatus = signal<number>(0);
  countryOrRegionSelected = signal<number>(0);
  scrolling = signal<boolean>(false);
  filter = signal<LocationFilter>(new LocationFilter());
  // public satelliteView = false;
  readonly satelliteView = signal(false);
  public statusFilterValue = output<string>();
  public filterStatusEnum = filterStatus;
  activeFilter = computed(() => this.assetsService.getFilter());

  // Computed signals
  overlayPlant: any;

  get OverlayPlant(): any {
    return this.overlayPlant;
  }

  set OverlayPlant(plant: any) {
    if (!plant) this.overlayPlant = null;
    else
      this.overlayPlant = Object.assign(
        {},
        {
          ...plant,
          filtered: false,
        }
      );
  }

  plants = computed(() => this.assetsService.plantsState().plants);

  currentLang = computed(() => this.i18n.languageFetched());

  assetCount = computed(() => {
    return this.visiblePlants().reduce(
      (total: number, plant: any) => total + this.assetsForPlant(plant).length,
      0
    );
  });

  visiblePlants = computed(() => {
    return this.plants().filter((plant: any) => this.assetsForPlant(plant).length > 0);
  });

  bcGroupedPlants = computed(() => {
    const grouped = groupBy(this.visiblePlants(), 'parentID');

    return this.sortByLocation(grouped);
  });

  sumCritical = computed(() =>
    this.plants().reduce((sum, plant) => sum + plant.Critical.length, 0)
  );

  sumWarning = computed(() => this.plants().reduce((sum, plant) => sum + plant.Warning.length, 0));

  sumInActive = computed(() =>
    this.plants().reduce((sum, plant) => sum + plant.InActive.length, 0)
  );

  sumWorking = computed(() => this.plants().reduce((sum, plant) => sum + plant.Working.length, 0));

  allWorking = computed(
    () =>
      this.assetCount() > 0 &&
      this.sumWorking() > 0 &&
      this.sumCritical() === 0 &&
      this.sumInActive() === 0 &&
      this.sumWarning() === 0 &&
      this.sumArchived() === 0
  );

  sumArchived = computed(() =>
    this.plants().reduce((sum, plant) => sum + plant.Archived.length, GLOBAL_CONSTANT.ZERO)
  );

  isMobile = computed(() => this.ui.IsMobile());
  isTablet = computed(() => this.ui.IsTablet());
  isDesktop = computed(() => this.ui.IsDesktop());
  isChrome = this.ui.IsChrome;

  // Effects
  routeEffect = effect(() => {
    this.route.params.subscribe((params) => this.onParamMapChange(params));
  });

  plantEffect = effect(() => {
    this.refresh();
    this.ui.updateBackBtn(false);
    this.ui.setBackText('');
    if (this.isMobile()) {
      this.onTabChanged(1);
    }
  });

  // Methods
  private getTabIndex(tab: string): number {
    return this.tabs.indexOf(tab) !== -1 ? this.tabs.indexOf(tab) : 0;
  }

  onParamMapChange(params: any) {
    if (!params) return;
    const tabIndex = this.getTabIndex(params['tab']);
    if (this.activeTab() !== tabIndex) this.activeTab.set(tabIndex);
    if (params['id']) this.onSelectCountryOrRegion(params['id']);
  }

  onSelectCountryOrRegion(id: any) {
    if (typeof id === 'string') id = +id;
    if (typeof id === 'number') this.countryOrRegionSelected.set(id);
  }

  refresh() {
    this.loading.set(true);
    try {
      const result: any = this.assetsService.plantsState();
      this.loading.set(false);
      this.loadingThumbnails.set(!result.loadingComplete);
      this.filteredPlants.set(result.filtered);
      this.setSelectionPreset();
      const currentFilter = this.assetsService.getFilter();
      this.homePageFilterStatus.set(0);
      this.filter.set(currentFilter);
    } catch (error) {
      logError(error);
      this.loading.set(false);
    }
  }

  breadcrumbs(asset: Asset) {
    return JSON.stringify(asset.breadcrumbs.map((breadcrumb: any) => breadcrumb.objectName));
  }

  onTabChanged(tab: number) {
    this.activeTab.set(tab);
    const routePath = [App_Routes.HOME, this.tabs[tab], this.countryOrRegionSelected()].filter(
      Boolean
    );
    this.router.navigate(routePath);
    document.getElementById('desktop-sidebar-inner')?.scrollTo({ top: GLOBAL_CONSTANT.ZERO });
  }

  setSelectionPreset() {
    const tempFilter = new LocationFilter();
    tempFilter.customer = this.filter().customer;

    const selectionPreset: any[] = [];
    const regions = this.assetsService.UnfilteredRegions.map((item: any) =>
      item.ApplyFilter(tempFilter)
    ).filter((item: any) => !!item && item.Assets.length > GLOBAL_CONSTANT.ZERO);
    const areas = regions.map((region: any) => region.children).flat(2);

    if (regions.length === 1) {
      selectionPreset.push(regions[0]);
      if (areas.length === 1) selectionPreset.push(areas[0]);
    }

    this.assetsService.updateSelectionPreset(selectionPreset);
  }

  // Filter methods
  onSetInactiveFilter() {
    this.assetsService.updateFilter(
      Object.assign(new LocationFilter(), {
        ...this.filter(),
        working: false,
        warning: false,
        critical: false,
        inactive: true,
        deInstalled: false,
      })
    );
  }

  onSetWorkingFineFilter() {
    this.assetsService.updateFilter(
      Object.assign(new LocationFilter(), {
        ...this.filter(),
        working: true,
        warning: false,
        critical: false,
        inactive: false,
        deInstalled: false,
      })
    );
  }

  onSetWarningFilter() {
    this.assetsService.updateFilter(
      Object.assign(new LocationFilter(), {
        ...this.filter(),
        working: false,
        warning: true,
        critical: false,
        inactive: false,
        deInstalled: false,
      })
    );
  }

  onSetCriticalFilter() {
    this.assetsService.updateFilter(
      Object.assign(new LocationFilter(), {
        ...this.filter(),
        working: false,
        warning: false,
        critical: true,
        inactive: false,
        deInstalled: false,
      })
    );
  }

  private normalizeEquipmentType(value: string | null | undefined): string {
    return (value ?? '').trim().toLowerCase();
  }

  private sortByLocation(countryArray: any[][]): any[][] {
    return countryArray.sort((a: any, b: any) => {
      const nameA = this.i18n.parse(a[0].breadcrumbs[1].objectName).toUpperCase();
      const nameB = this.i18n.parse(b[0].breadcrumbs[1].objectName).toUpperCase();
      return nameA.localeCompare(nameB);
    });
  }

  private assetsForPlant(plant: any): any[] {
    const filter = this.activeFilter();

    return (plant?.Assets ?? []).filter((asset: any) => this.matchesActiveFilter(asset, filter));
  }

  private matchesActiveFilter(asset: any, filter: any): boolean {
    if (!asset || !filter) {
      return false;
    }

    const isDeInstalled = asset.isArchived === true || asset.deInstalled === true;

    // Status filter
    let matchesStatus = true;

    if (filter.deInstalled === true) {
      matchesStatus = isDeInstalled;
    } else if (filter.inactive === true) {
      matchesStatus = !isDeInstalled && asset.inactive === true;
    } else if (filter.critical === true) {
      matchesStatus = !isDeInstalled && asset.inactive !== true && asset.critical === true;
    } else if (filter.warning === true) {
      matchesStatus =
        !isDeInstalled &&
        asset.inactive !== true &&
        asset.critical !== true &&
        asset.warning === true;
    } else if (filter.working === true) {
      matchesStatus =
        !isDeInstalled &&
        asset.inactive !== true &&
        asset.critical !== true &&
        asset.warning !== true;
    }

    if (!matchesStatus) {
      return false;
    }

    // Equipment-type filter
    const selectedTypes: string[] = filter.selectedTypes ?? [];

    if (selectedTypes.length > 0) {
      const equipmentType = this.normalizeEquipmentType(asset.equipmentType);

      const matchesEquipmentType = selectedTypes.some(
        (selectedType: string) => this.normalizeEquipmentType(selectedType) === equipmentType
      );

      if (!matchesEquipmentType) {
        return false;
      }
    }

    // Optional customer filter
    if (filter.customer && asset.customerName !== filter.customer) {
      return false;
    }

    return true;
  }

  onSetArchivedFilter() {
    this.assetsService.updateFilter(
      Object.assign(new LocationFilter(), {
        ...this.filter(),
        working: false,
        warning: false,
        critical: false,
        inactive: false,
        deInstalled: true,
      })
    );
  }

  onMapMarkerClick(object: any) {
    this.router.navigate([App_Routes.PLANT, object.objectID]);
  }

  setStatusFilterValue(value: any) {
    this.homePageFilterStatus.set(value);
  }
}
